import type { ComponentProps } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import { getDaysUntil } from "@/v2-backoffice-client/license/license.utils"
import {
  TRIAL_CLOCK_LABELS,
  type TrialClockState,
} from "@/v2-backoffice-client/license/trials/license.trials.constants"
import type { TrialClock } from "@/v2-backoffice-client/license/trials/license.trials.utils"

export type LicenseTrialClockProps = ComponentProps<"div"> & {
  clock: TrialClock | null
  state: TrialClockState
  /** The trial's start, for a clock that has not begun. */
  startedAt: string | null
}

/**
 * The trial as a timeline: a track from start to end with the elapsed share
 * filled, and the countdown beside it. The fill turns amber in the last
 * days and red once the end date has passed with the trial still open —
 * the row that needs a decision reads as such from across the room. No
 * track without a start date (nothing to measure from); no bar at all for
 * an open-ended trial.
 */
function LicenseTrialClock(props: LicenseTrialClockProps) {
  const { clock, state, startedAt, className, ...divProps } = props

  if (state === "not-started" && startedAt) {
    const days = getDaysUntil(startedAt)
    return (
      <div
        className={cn("license-trial-clock", className)}
        title={`Starts ${formatDateString(startedAt)}`}
        {...divProps}
      >
        <span className="license-trial-clock-label text-muted-foreground">
          Starts in {days} {days === 1 ? "day" : "days"}
        </span>
      </div>
    )
  }

  if (!clock) {
    return (
      <div className={cn("license-trial-clock", className)} {...divProps}>
        <span className="license-trial-clock-label text-muted-foreground">
          {TRIAL_CLOCK_LABELS["open-ended"]}
        </span>
      </div>
    )
  }

  const { facts, progress, totalDays } = clock
  const title = [
    clock.startedAt ? `Started ${formatDateString(clock.startedAt)}` : null,
    `${facts.isOverdue ? "Ended" : "Ends"} ${formatDateString(clock.endsAt)}`,
    totalDays != null ? `${totalDays}-day trial` : null,
  ]
    .filter(Boolean)
    .join(" · ")

  return (
    <div
      className={cn(
        "license-trial-clock",
        `license-trial-clock--${state}`,
        className,
      )}
      title={title}
      {...divProps}
    >
      {progress != null && (
        <span className="license-trial-clock-track" aria-hidden>
          <span
            className="license-trial-clock-fill"
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </span>
      )}
      <span className="license-trial-clock-label">{facts.label}</span>
    </div>
  )
}

export default LicenseTrialClock
